import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Seo from "../components/Seo";

const times = ["10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"];

export default function BookSiteVisit() {
  const [sp] = useSearchParams();
  const propId = sp.get("property");
  const [form, setForm] = useState({ name: "", mobile: "", email: "", preferred_date: "", preferred_time: "", message: "" });
  const [done, setDone] = useState(false);

  const { data: prop } = useQuery({ queryKey: ["visit-prop", propId], queryFn: async () => (await api.get(`/properties/${propId}`)).data, enabled: !!propId });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.preferred_date || !form.preferred_time) return toast.error("Please fill name, mobile, date and time");
    try {
      await api.post("/site-visits", { ...form, property_id: propId, property_title: prop?.title || null });
      setDone(true); toast.success("Site visit requested!");
    } catch { toast.error("Failed to submit. Try again."); }
  };

  if (done) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <div className="w-16 h-16 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8" /></div>
      <h1 className="font-heading text-2xl font-bold text-slate-900 mt-5">Site Visit Requested</h1>
      <p className="text-slate-500 mt-2">Our team will confirm your visit shortly over call/WhatsApp.</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <Seo title="Book a Site Visit | Faridabad Property Vault" description="Book an assisted site visit for a verified Faridabad property." />
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Calendar className="w-7 h-7 text-indigo-700" /> Book a Site Visit</h1>
      {prop && <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-sm text-indigo-900"><b>{prop.title}</b> — {prop.sector}, Faridabad ({prop.property_id})</div>}
      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-4" data-testid="visit-form">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" data-testid="visit-name" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
        <div className="grid sm:grid-cols-2 gap-4">
          <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile *" data-testid="visit-mobile" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" data-testid="visit-email" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-slate-600">Preferred Date *</label>
            <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="visit-date" className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 text-sm" /></div>
          <div><label className="text-sm text-slate-600">Preferred Time *</label>
            <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
              <SelectTrigger className="mt-1" data-testid="visit-time"><SelectValue placeholder="Select time" /></SelectTrigger>
              <SelectContent>{times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything specific? (optional)" rows={3} data-testid="visit-message" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
        <Button type="submit" data-testid="visit-submit" className="w-full bg-indigo-900 hover:bg-indigo-800">Request Site Visit</Button>
      </form>
    </div>
  );
}
